import { useRef, useState } from "react";
import { useLoop, useSim, Slider, SimBtn, Stage, Readout } from "./simKit";

export const HoverSim = ({ onSuccess }) => {
  const [s, setS, sRef] = useSim({ alt: 10, vel: 0, held: 0 });
  const [thr, setThr] = useState(45);
  const [auto, setAuto] = useState(false);
  const [gain, setGain] = useState(0.4);
  const TARGET = 60;
  useLoop((dt) => { const p = sRef.current; setS((() => {
    const thrust = auto ? Math.max(0, Math.min(100, 50 + gain * (TARGET - p.alt) - 8 * gain * p.vel)) : thr;
    let vel = p.vel + ((thrust - 50) * 0.6 - p.vel * 0.8) * dt;
    let alt = p.alt + vel * dt * 10;
    if (alt <= 0) { alt = 0; vel = Math.max(0, vel); }
    if (alt >= 100) { alt = 100; vel = Math.min(0, vel); }
    const steady = Math.abs(alt - TARGET) < 3 && Math.abs(vel) < 0.3;
    const held = steady ? p.held + dt : 0;
    if (held > 2) onSuccess();
    return { alt, vel, held };
  })()); }, true);
  const y = 300 - s.alt * 2.6;
  return (
    <div className="space-y-4">
      <Stage>
        <line x1="40" x2="560" y1={300 - TARGET * 2.6} y2={300 - TARGET * 2.6} stroke="#FFD166" strokeDasharray="8 6" strokeWidth="2" />
        <text x="44" y={292 - TARGET * 2.6} fontSize="12" fill="#B7791F">target height</text>
        <rect x="240" y="310" width="120" height="10" rx="5" fill="#0A192F" opacity=".2" />
        <g transform={`translate(300 ${y})`}>
          <rect x="-50" y="-6" width="100" height="12" rx="6" fill="#0A192F" />
          {[-55, 55].map((x) => <ellipse key={x} cx={x} cy="-12" rx="26" ry="5" fill="#007A8A" opacity=".7" />)}
          <rect x="-16" y="-14" width="32" height="20" rx="6" fill="#FF2E63" />
        </g>
      </Stage>
      <Readout items={[["Height", s.alt.toFixed(0)], ["Speed", s.vel.toFixed(2)], ["Status", Math.abs(s.alt - TARGET) < 3 ? "On target" : "Adjusting", Math.abs(s.alt - TARGET) < 3 ? "good" : ""]]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Slider label="Throttle (manual)" value={thr} min={0} max={100} onChange={setThr} testid="sim-throttle" unit="%" />
        <Slider label="Controller gain" value={gain} min={0.05} max={1.5} step={0.05} onChange={setGain} testid="sim-gain" />
      </div>
      <SimBtn tone={auto ? "pink" : "light"} onClick={() => setAuto(!auto)} testid="sim-auto-toggle">Auto-hold: {auto ? "ON" : "OFF"}</SimBtn>
    </div>
  );
};

export const MixerSim = ({ onSuccess }) => {
  const [c, setC] = useState({ pitch: 0, roll: 0, yaw: 0 });
  const tried = useRef({});
  const set = (k) => (v) => {
    setC((p) => ({ ...p, [k]: v }));
    if (Math.abs(v) >= 0.5) tried.current[k] = true;
    if (Object.keys(tried.current).length === 3) onSuccess();
  };
  const T = 50, { pitch: p, roll: r, yaw: y } = c;
  // +pitch speeds rear motors, +roll speeds left motors, +yaw speeds the FL/RR pair
  const m = { FL: T - p * 20 + r * 20 + y * 15, FR: T - p * 20 - r * 20 - y * 15, RL: T + p * 20 + r * 20 - y * 15, RR: T + p * 20 - r * 20 + y * 15 };
  const pos = { FL: [200, 80], FR: [400, 80], RL: [200, 260], RR: [400, 260] };
  return (
    <div className="space-y-4">
      <Stage>
        <g transform={`rotate(${y * 25} 300 170)`}>
          <line x1="200" y1="80" x2="400" y2="260" stroke="#0A192F" strokeWidth="10" strokeLinecap="round" />
          <line x1="400" y1="80" x2="200" y2="260" stroke="#0A192F" strokeWidth="10" strokeLinecap="round" />
          <rect x="270" y="145" width="60" height="50" rx="12" fill="#FF2E63" />
          <text x="300" y="140" textAnchor="middle" fontSize="12" fill="#0A192F">FRONT</text>
          {Object.entries(pos).map(([k, [x, yy]]) => (
            <g key={k}>
              <circle cx={x} cy={yy} r="40" fill="#007A8A" opacity={0.15 + (m[k] / 100) * 0.85} />
              <text x={x} y={yy + 5} textAnchor="middle" fontSize="18" fontWeight="700" fill="#fff">{`${m[k].toFixed(0)}%`}</text>
              <text x={x} y={yy + 58} textAnchor="middle" fontSize="15" fill="#0A192F">{k}</text>
            </g>))}
        </g>
      </Stage>
      <div className="grid sm:grid-cols-3 gap-4">
        <Slider label="Pitch (forward +)" value={p} min={-1} max={1} step={0.05} onChange={set("pitch")} testid="sim-pitch" />
        <Slider label="Roll (right +)" value={r} min={-1} max={1} step={0.05} onChange={set("roll")} testid="sim-roll" />
        <Slider label="Yaw (clockwise +)" value={y} min={-1} max={1} step={0.05} onChange={set("yaw")} testid="sim-yaw" />
      </div>
      <p className="text-xs text-slate-500">Challenge: push each axis past ±0.5 at least once.</p>
    </div>
  );
};

export const RocketSim = ({ onSuccess }) => {
  const [angle, setAngle] = useState(40);
  const [speed, setSpeed] = useState(55);
  const [fl, setFl, flRef] = useSim(null);
  const [target] = useState(() => 300 + Math.round(Math.random() * 200));
  const [res, setRes] = useState("");
  const [path, setPath] = useState(null);
  const G = 9.8, SC = 0.9;
  useLoop((dt) => { const f = flRef.current; setFl((() => {
    if (!f) return f;
    const t = f.t + dt * 2.2, a = (angle * Math.PI) / 180;
    const x = speed * Math.cos(a) * t * SC, y = (speed * Math.sin(a) * t - 0.5 * G * t * t) * SC;
    if (y < 0 && t > 0.1) {
      const hit = Math.abs(40 + x - target) < 30;
      setRes(hit ? "hit" : x + 40 < target ? "short" : "long");
      if (hit) onSuccess();
      setPath(f.pts);
      return null;
    }
    return { t, pts: [...f.pts, [40 + x, 310 - y]] };
  })()); }, !!fl);
  const shown = fl?.pts || path;
  const last = shown ? shown[shown.length - 1] : [40, 310];
  return (
    <div className="space-y-4">
      <Stage>
        <rect x="0" y="310" width="600" height="30" fill="#CFE3D4" />
        <rect x={target - 30} y="304" width="60" height="8" rx="4" fill="#FF2E63" />
        <text x={target} y="298" textAnchor="middle" fontSize="12" fill="#C2163F">TARGET</text>
        {(fl || path) && <polyline points={(fl ? fl.pts : path).map((p) => p.join(",")).join(" ")} fill="none" stroke="#007A8A" strokeWidth="2.5" strokeDasharray="5 4" />}
        <g transform={`translate(${last[0]} ${last[1]}) rotate(${-angle})`}><rect x="-4" y="-6" width="26" height="12" rx="6" fill="#0A192F" /><polygon points="22,-6 32,0 22,6" fill="#FF2E63" /></g>
      </Stage>
      <Readout items={[["Result", res === "hit" ? "Target hit!" : res === "short" ? "Too short" : res === "long" ? "Too far" : "Ready", res === "hit" ? "good" : res ? "bad" : ""]]} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Slider label="Launch angle" value={angle} min={10} max={80} onChange={setAngle} testid="sim-angle" unit="°" />
        <Slider label="Launch speed" value={speed} min={30} max={80} onChange={setSpeed} testid="sim-speed" />
      </div>
      <SimBtn onClick={() => { setRes(""); setPath(null); setFl({ t: 0, pts: [[40, 310]] }); }} disabled={!!fl} testid="sim-launch">Launch</SimBtn>
    </div>
  );
};

export const OrbitSim = ({ onSuccess }) => {
  const [v, setV] = useState(5);
  const [st, setSt, stRef] = useSim(null);
  const [res, setRes] = useState("");
  const GM = 4000, R = 40, C = [300, 170];
  useLoop((dt) => { const s = stRef.current; setSt((() => {
    if (!s) return s;
    let { x, y, vx, vy, ang, prev, trail } = s;
    for (let i = 0; i < 8; i++) {
      const h = dt * 4, r = Math.hypot(x, y), a = -GM / (r * r * r);
      vx += a * x * h; vy += a * y * h; x += vx * h; y += vy * h;
    }
    const r = Math.hypot(x, y), th = Math.atan2(y, x);
    let d = th - prev; if (d > Math.PI) d -= 2 * Math.PI; if (d < -Math.PI) d += 2 * Math.PI;
    ang += Math.abs(d);
    if (r < R) { setRes("crash"); return null; }
    if (r > 400) { setRes("escape"); return null; }
    if (ang >= 2 * Math.PI && s.ang < 2 * Math.PI) { setRes("orbit"); onSuccess(); }
    return { x, y, vx, vy, ang, prev: th, trail: [...trail.slice(-300), [C[0] + x, C[1] + y]] };
  })()); }, !!st);
  return (
    <div className="space-y-4">
      <Stage dark>
        {[...Array(30)].map((_, i) => <circle key={i} cx={(i * 97) % 600} cy={(i * 53) % 340} r="1" fill="#fff" opacity=".5" />)}
        <circle cx={C[0]} cy={C[1]} r={R} fill="#1E6FD9" /><circle cx={C[0] - 10} cy={C[1] - 8} r="14" fill="#3BB273" opacity=".8" />
        {st && <polyline points={st.trail.map((p) => p.join(",")).join(" ")} fill="none" stroke="#4FD1C5" strokeWidth="2" />}
        {st && <rect x={C[0] + st.x - 5} y={C[1] + st.y - 5} width="10" height="10" fill="#FFD166" />}
      </Stage>
      <Readout items={[["Result", res === "orbit" ? "Stable orbit!" : res === "crash" ? "Fell back to Earth" : res === "escape" ? "Escaped Earth" : st ? "Flying…" : "Ready", res === "orbit" ? "good" : res ? "bad" : ""]]} />
      <Slider label="Launch speed" value={v} min={3} max={14} step={0.1} onChange={setV} testid="sim-orbit-speed" />
      <SimBtn onClick={() => { setRes(""); setSt({ x: 0, y: -110, vx: v, vy: 0, ang: 0, prev: -Math.PI / 2, trail: [] }); }} testid="sim-launch">Launch satellite</SimBtn>
    </div>
  );
};
